import { Component, OnInit, Input, ViewChild } from "@angular/core";
import { Table } from "primeng/table";
import {
  UserRoles,
  CurrentUser,
} from "../../shared/services/current-user.service";
import { RequestStatusEnum } from "../../shared/services/request-status.service";
import { AlertService } from "../../shared/services/alert.service";
import { LoadingWrapperService } from "../../shared/services/loading-wrapper.service";
import { finalize } from "rxjs/operators";
import { FileSaverService } from "../../shared/services/file-saver.service";
import { HttpErrorResponse } from "@angular/common/http";
import {
  Column,
  DropdownOption,
  PaginationModel,
  RequestGridModel,
} from "./request-list.model";
import { ViewRequestOptions } from "../../shared/resolvers/view-requests.resolver";
import { LazyLoadEvent } from "primeng/api";
import { format } from "date-fns";
import {
  RequestGridQueryCriteria,
  RequestGridService,
  RequestGridSortField,
} from "../../shared/services/request-grid.service";
import { RequestService } from "../../shared/services/request.service";
import { YesNoPipe } from "../../shared/pipes/yesNo.pipe";
import { RequestPurposeType } from "../../shared/services/request-purpose.service";

@Component({
  selector: "app-request-list",
  templateUrl: "./request-list.component.html",
  styleUrls: ["./request-list.component.css"],
})
export class RequestListComponent implements OnInit {
  @ViewChild("dt") public dataTable: Table;
  @Input() public currentUser: CurrentUser;
  @Input() public requestOptions: ViewRequestOptions;

  public displayedRequests: RequestGridModel[] = [];
  public selectedRequests: RequestGridModel[] = [];
  public isLoading: boolean = true;
  public requestCount: number = 0;
  public currentPage: number = 0;
  public firstIndexInPage: number = 0;
  public numberOfRowsPerPage: number = 15;
  public cols: Column[];
  public dateRange: Date[];
  public editRoles: UserRoles =
    UserRoles.Requestor |
    UserRoles.CollaboratorLiaison |
    UserRoles.TacticalLiaison |
    UserRoles.ExperimentCreator;

  public yesNoDropdownOptions: DropdownOption<boolean>[] = [
    { label: "", value: null },
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];

  public requestStatusDropdownOptions: DropdownOption<RequestStatusEnum>[] = [];

  public requestPurposeDropdownOptions: DropdownOption<RequestPurposeType>[] =
    [];

  public get areTableOperationsEnabled(): boolean {
    return this.selectedRequests && this.selectedRequests.length > 0;
  }

  constructor(
    private alertService: AlertService,
    private loadingService: LoadingWrapperService,
    private requestService: RequestService,
    private requestGridService: RequestGridService,
    private fileSaverService: FileSaverService
  ) {}

  ngOnInit() {
    this.cols = [
      { field: "requestNumber", header: "Request #", sortable: true },
      { field: "title", header: "Experiment Title", sortable: true },
      { field: "status", header: "Status", sortable: false },
      { field: "requester", header: "Requester", sortable: false },
      { field: "delegate", header: "Delegate", sortable: false },
      { field: "tacticalLiaison", header: "Assigned To", sortable: false },
      { field: "crop", header: "Crop", sortable: false },
      { field: "plantDate", header: "Plant Date", sortable: true },
      { field: "requestPurpose", header: "Purpose", sortable: false },
      { field: "isSamplingRequired", header: "Sampling?", sortable: false },
      { field: "isRecurringRequest", header: "Recurring?", sortable: false },
    ];
    const orderedRequestStatuses = this.requestOptions.requestStatuses.sort((a, b) => a.order - b.order);
    this.requestStatusDropdownOptions = orderedRequestStatuses.map(
      (s) =>
        <DropdownOption<RequestStatusEnum>>{
          label: s.name,
          value: s.id,
        }
    );

    this.requestPurposeDropdownOptions =
      this.requestOptions.requestPurposes.map(
        (s) =>
          <DropdownOption<RequestPurposeType>>{
            label: s.name,
            value: s.id,
          }
      );
  }

  public loadRequests(event: LazyLoadEvent) {
    const sortField: RequestGridSortField =
      RequestGridSortField[event.sortField] || RequestGridSortField.plantDate;

    const criteria: RequestGridQueryCriteria = {
      skip: event.first,
      take: event.rows,
      sortField: sortField,
      sortOrder: event.sortOrder,
    };

    if (event.filters) {
      criteria.requestNumber =
        event.filters.requestNumber && event.filters.requestNumber.value;
      criteria.title = event.filters.title && event.filters.title.value;
      criteria.statusIds = event.filters.status && event.filters.status.value;
      criteria.requester =
        event.filters.requester && event.filters.requester.value;
      criteria.delegate =
        event.filters.delegate && event.filters.delegate.value;
      criteria.tacticalLiaison =
        event.filters.tacticalLiaison && event.filters.tacticalLiaison.value;
      criteria.crop = event.filters.crop && event.filters.crop.value;
      criteria.purposeIds =
        event.filters.requestPurpose && event.filters.requestPurpose.value;
      criteria.isRecurringRequest =
        event.filters.isRecurringRequest &&
        event.filters.isRecurringRequest.value;
      criteria.isSamplingRequired =
        event.filters.isSamplingRequired &&
        event.filters.isSamplingRequired.value;
    }

    if (this.dateRange) {
      criteria.plantStartDate = this.dateRange[0];
      criteria.plantEndDate = this.dateRange[1];
    }

    this.isLoading = true;

    this.requestGridService
      .query(criteria)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        (response) => {
          this.displayedRequests = response.data.map((r) => ({
            ...r,
            status: this.getStatus(r),
            requester: this.getUserName(r.requesterId),
            delegate: this.getUserName(r.delegateId),
            tacticalLiaison: this.getUserName(r.tacticalLiaisonId),
            crop: this.getCropName(r.cropId),
            requestPurpose: this.getRequestPurposeName(r.requestPurposeId),
          }));
          this.requestCount = response.totalCount;
        },
        (err) => {
          this.alertService.error(`Unable to load Requests`);
        }
      );
  }

  public printPlantingCards() {
    const [sortField, sortOrder] = [
      this.dataTable.sortField,
      this.dataTable.sortOrder,
    ];
    const yesNoPipe = new YesNoPipe();

    const requestIds = this.selectedRequests
      .sort((request1, request2) => {
        const prop1 = request1[sortField];
        const prop2 = request2[sortField];
        let difference = 0;

        if (prop1 == null && prop2 != null) {
          difference = -1;
        } else if (prop1 != null && prop2 == null) {
          difference = 1;
        } else if (prop1 == null && prop2 == null) {
          difference = 0;
        } else if (typeof prop1 === "string" && typeof prop2 === "string") {
          difference = prop1.localeCompare(prop2);
        } else if (prop1 instanceof Date && prop2 instanceof Date) {
          difference = prop1.getTime() - prop2.getTime();
        } else if (typeof prop1 === "number" && typeof prop2 === "number") {
          difference = prop1 - prop2;
        } else if (typeof prop1 === "boolean" && typeof prop2 === "boolean") {
          const prop1String = yesNoPipe.transform(prop1);
          const prop2String = yesNoPipe.transform(prop2);

          difference = prop1String.localeCompare(prop2String);
        }

        if (difference === 0) {
          difference = request1.id - request2.id;
        }

        return sortOrder * difference;
      })
      .map((request) => request.id);

    this.loadingService.setMessage("Printing Planting Cards...");

    this.requestService
      .getPlantingCards(requestIds)
      .pipe(
        finalize(() => {
          this.loadingService.clearMessage();
          this.selectedRequests = [];
        })
      )
      .subscribe(
        (plantingCardsFile) => {
          this.fileSaverService.save(plantingCardsFile);
          this.alertService.success("Planting Card Download Complete!");
        },
        (errorResponse: HttpErrorResponse) => {
          this.alertService.error(
            `Error retreiving Planting Cards. ${errorResponse.error}.`
          );
        }
      );
  }

  public onPageChange(event: PaginationModel) {
    this.firstIndexInPage = event.first;
    this.numberOfRowsPerPage = event.rows;
    this.currentPage = this.firstIndexInPage / this.numberOfRowsPerPage;
    this.selectedRequests = [];
  }

  public resetCurrentPage() {
    this.firstIndexInPage = 0;
    this.currentPage = 0;
    this.selectedRequests = [];
  }

  public onSelectedRequestsChange(selectedRequests: RequestGridModel[]) {
    let filteredRequests = selectedRequests.slice();

    // Only select current page if select all button is clicked.
    if (filteredRequests.length > this.numberOfRowsPerPage) {
      const startIndex = this.currentPage * this.numberOfRowsPerPage;
      const endIndex = startIndex + this.numberOfRowsPerPage;

      filteredRequests = filteredRequests.slice(startIndex, endIndex);
    }

    this.selectedRequests = filteredRequests.filter((request) =>
      this.allowPlantingCardPrintingForRequest(request)
    );
  }

  public allowPlantingCardPrintingForRequest(
    request: RequestGridModel
  ): boolean {
    const validRequestStatuses: RequestStatusEnum[] = [
      RequestStatusEnum.ReadyForReview,
      RequestStatusEnum.Reviewed,
      RequestStatusEnum.CreatingExperiment,
      RequestStatusEnum.ExperimentInProgress,
      RequestStatusEnum.Pollination,
      RequestStatusEnum.Harvest,
      RequestStatusEnum.PlantedInFlat,
    ];
    return validRequestStatuses.some((status) => status === request.statusId);
  }

  public getEditLink(id: number) {
    return `/requests/edit/${id}`;
  }

  public getViewLink(id: number) {
    return `/requests/view/${id}`;
  }

  private getStatus(request: RequestGridModel): string {
    let displayDateStatuses = [
      RequestStatusEnum.Pollination,
      RequestStatusEnum.Harvest,
      RequestStatusEnum.PlantedInFlat,
    ];
    if (displayDateStatuses.includes(request.statusId)) {
      const date: string = request.latestStatusChangeDate
        ? format(request.latestStatusChangeDate, "M/D/YY")
        : "";
      return `${
        this.requestOptions.requestStatusDitionary[request.statusId].name
      } - ${date}`;
    } else {
      return this.requestOptions.requestStatusDitionary[request.statusId].name;
    }
  }

  private getUserName(userId?: number): string {
    const user = this.requestOptions.userDictionary[userId];

    if (user) {
      return `${user.surname}, ${user.givenName}`;
    }

    return "";
  }

  private getCropName(cropId?: number): string {
    const crop = this.requestOptions.cropDictionary[cropId];

    if (crop) {
      return crop.name;
    }

    return "";
  }

  private getRequestPurposeName(requestPurposeId?: number): string {
    const requestPurpose =
      this.requestOptions.requestPurposeDictionary[requestPurposeId];

    if (requestPurpose) {
      return requestPurpose.name;
    }

    return "";
  }
}



// WEBPACK FOOTER //
// F:/PHAZP5152IS01-4/_work/202/s/src/app/view-request/request-list/request-list.component.ts